import React, { useEffect } from 'react';

import styled from '@emotion/styled';

import {
  Box,
  Button,
  COLORS,
  Carousel,
  Text,
  useCarousel,
} from '@clutter/clean';

import { TOKENS } from '@root/components/www/home/tokens';
import { useTrack } from '@root/initializers/wt';

import { Ratings } from '@shared/ratings';

import { useHasIntersected } from '@utils/hooks';

import { ReviewCard } from './review_card';
import { ReviewData } from './review_carousel/data';
import { Arrow } from '@images/svg/arrow';

const NavButton = styled(Button)`
  width: 56px;
  height: 56px;
  padding: 16px;
  margin-top: 28px;
  min-width: 0;
`;

const DarkCarouselRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: ${TOKENS.space[16]};
`;

const DarkSlideArea = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const DarkNavButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border: 1px solid ${TOKENS.color.neutral0_10};
  border-radius: ${TOKENS.radius.pill};
  background: ${TOKENS.color.neutral0_5};
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background: ${TOKENS.color.neutral0_10};
    border-color: ${TOKENS.color.neutral0_20};
  }
`;

export const ReviewCarousel: React.FC<{
  title?: string;
  background?: string;
  reviews: ReviewData[];
  variant?: 'default' | 'dark';
}> = ({ background, reviews, title, variant = 'default' }) => {
  const isDark = variant === 'dark';
  const resolvedBackground =
    background ?? (isDark ? TOKENS.color.sectionBgDark : COLORS.tealBackground);
  const { targetRef, hasIntersected } = useHasIntersected({ threshold: 0.5 });
  const track = useTrack({
    container: 'testimonials',
  });
  const props = useCarousel({ draggable: false });
  const { next, prev } = props;

  useEffect(() => {
    if (hasIntersected) {
      track({ action: 'view' });
    }
  }, [track, hasIntersected]);

  const handlePrev = () => {
    track({ objectName: 'previous', action: 'click', objectType: 'button' });
    prev();
  };

  const handleNext = () => {
    track({ objectName: 'next', action: 'click', objectType: 'button' });
    next();
  };

  const arrowColor = isDark ? TOKENS.color.onDark : COLORS.cloud;

  if (isDark) {
    return (
      <Box
        ref={targetRef}
        background={resolvedBackground}
        color={TOKENS.color.onDark}
        padding={['64px 24px', null, '80px 48px']}
        textAlign="center"
      >
        <Box.Flex flexDirection="column" gap="48px" alignItems="center">
          <DarkCarouselRow>
            <DarkNavButton
              type="button"
              onClick={handlePrev}
              aria-label="Previous testimonial"
            >
              <Arrow fillColor={arrowColor} />
            </DarkNavButton>
            <DarkSlideArea>
              <Carousel {...props} slidesToShow={1}>
                {reviews.map((testimonial, key) => (
                  <ReviewCard
                    key={key}
                    title={testimonial.title}
                    quote={testimonial.quote}
                    reviewer={testimonial.reviewer}
                    onDark
                  />
                ))}
              </Carousel>
            </DarkSlideArea>
            <DarkNavButton
              type="button"
              onClick={handleNext}
              aria-label="Next testimonial"
            >
              <Arrow fillColor={arrowColor} direction="right" />
            </DarkNavButton>
          </DarkCarouselRow>
          <Ratings onDark />
        </Box.Flex>
      </Box>
    );
  }

  return (
    <Box
      ref={targetRef}
      background={resolvedBackground}
      color={COLORS.tealDark}
      padding={['46px 24px', null, '54px']}
      textAlign="center"
    >
      <Box.Flex flexDirection="column" gap="24px">
        {title && <Text.Title size="large">{title}</Text.Title>}
        <Box.Flex alignItems="flex-start">
          <NavButton onClick={handlePrev} aria-label="previous slide">
            <Arrow fillColor={arrowColor} />
          </NavButton>
          <Carousel {...props} slidesToShow={1}>
            {reviews.map((testimonial, key) => (
              <ReviewCard
                key={key}
                title={testimonial.title}
                quote={testimonial.quote}
                reviewer={testimonial.reviewer}
              />
            ))}
          </Carousel>
          <NavButton onClick={handleNext} aria-label="next slide">
            <Arrow fillColor={arrowColor} direction="right" />
          </NavButton>
        </Box.Flex>
        <Ratings />
      </Box.Flex>
    </Box>
  );
};
